/** @returns {number|null} */
export function toNum(n) {
    const x = Number(n);
    return Number.isFinite(x) ? x : null;
}

/**
 * Универсальный парсер маркапов:
 * - новый формат: {default, ranges:[{f,t,p}]}
 * - старый формат: [{from,to,value}]
 * - или JSON строка любого из них
 *
 * Возвращает { def: number|null, ranges: Array<{f:number, t:number|null, p:number}> }
 */
export function parseMarkupConfig(input) {
    let v = input;

    if (typeof v === 'string' && v.trim()) {
        try { v = JSON.parse(v); } catch { /* ignore */ }
    }

    // new format
    if (v && typeof v === 'object' && !Array.isArray(v) && Array.isArray(v.ranges)) {
        const def = toNum(v.default);

        const ranges = v.ranges
            .map(r => ({
                f: toNum(r?.f),
                t: r?.t === null ? null : toNum(r?.t),
                p: toNum(r?.p),
            }))
            .filter(r =>
                r.f !== null &&
                r.f >= 0 &&
                r.p !== null &&
                (r.t === null || (r.t !== null && r.t >= r.f))
            )
            .sort((a, b) => a.f - b.f);

        return { def, ranges };
    }

    // old format array
    if (Array.isArray(v)) {
        const ranges = v
            .map(r => ({
                f: toNum(r?.from),
                t: toNum(r?.to),
                p: toNum(r?.value),
            }))
            .filter(r => r.f !== null && r.t !== null && r.p !== null)
            .sort((a, b) => a.f - b.f);

        return { def: null, ranges };
    }

    return { def: null, ranges: [] };
}

/**
 * Находит процент наценки по цене.
 * t — ВКЛЮЧИТЕЛЬНО (price <= t)
 * Если есть конфликт на границе (price подходит нескольким диапазонам),
 * побеждает диапазон с БОЛЬШИМ f.
 */
export function findMarkupPercentInclusive(price, config) {
    const ranges = (config?.ranges || []).slice();

    // приоритет: больший f выигрывает (решает конфликты на границе)
    ranges.sort((a, b) => (b.f ?? -Infinity) - (a.f ?? -Infinity));

    for (const r of ranges) {
        const f = r.f;
        const t = r.t;

        if (f === null) continue;

        const ok = (price >= f) && (t === null || price <= t);
        if (ok) return r.p ?? null;
    }

    return null;
}

/**
 * purchasePrice -> sellPrice по markupConfig:
 * - берём p из ranges (если >0), иначе default (если >0), иначе null
 */
export function computeSellPrice(purchasePrice, markupConfig) {
    const price = toNum(purchasePrice);
    if (price === null) return null;

    const cfg = parseMarkupConfig(markupConfig);

    const hitP = findMarkupPercentInclusive(price, cfg);
    if (hitP !== null && hitP > 0) {
        return price * (1 + hitP / 100);
    }

    const defP = toNum(cfg.def);
    if (defP !== null && defP > 0) {
        return price * (1 + defP / 100);
    }

    return null;
}

/**
 * Роутер по контексту для locationData (новые поля *_markup)
 */
export function computeByContext(
    purchasePrice,
    locationData,
    context,
    useAdvancedMarkup = true
) {
    const bd = locationData || {}

    const map = {
        orders: bd.orders_markup,
        estimates: bd.estimates_markup,
        warehouse: bd.warehouse_markup,
    }

    let cfg = map[context]

    if (!cfg) return null

    if (!useAdvancedMarkup) {
        const parsed = parseMarkupConfig(cfg)

        cfg = {
            default: parsed.def,
            ranges: [],
        }
    }

    return computeSellPrice(purchasePrice, cfg)
}

/** Нормализация ввода цены: '-', min/max, NaN → null */
export function normalizePriceInput(value, { min = 0, max = 1_000_000 } = {}) {
    if (value === '-') return null;
    const n = toNum(value);
    if (n === null) return null;
    if (n < min) return min;
    if (n > max) return max;
    return n;
}
